import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Progress } from "src/components/ui/Progress";
import { CheckCircle, Circle } from "lucide-react";
import { TutorExtendedProfileData } from "src/interfaces/TutorProfile";

interface TutorProfileCompletenessSectionProps {
  profileData: TutorExtendedProfileData;
}

const TutorProfileCompletenessSection: React.FC<TutorProfileCompletenessSectionProps> = ({
  profileData
}) => {
  // Calculate profile completeness
  const calculateCompleteness = () => {
    const fields = [
      { name: "Profile Picture", value: profileData.profilePictureUrl },
      { name: "Bio", value: profileData.bio },
      { name: "Hourly Rate", value: profileData.hourly_rate },
      { name: "Teaching Experience", value: profileData.experience && profileData.experience.length > 0 },
      { name: "Subjects Taught", value: profileData.subjects_taught?.length > 0 },
      { name: "Education", value: profileData.education?.length > 0 },
      { name: "Achievements", value: profileData.achievements?.length > 0 },
      { name: "Location", value: profileData.location },
    ];

    const completedFields = fields.filter(field => {
      if (typeof field.value === 'string') {
        return field.value && field.value.trim() !== '';
      }
      return Boolean(field.value);
    });

    const percentage = Math.round((completedFields.length / fields.length) * 100);

    return {
      percentage,
      completedFields: completedFields.length,
      totalFields: fields.length,
      fields: fields.map(field => ({
        ...field,
        completed: typeof field.value === 'string' 
          ? field.value && field.value.trim() !== ''
          : Boolean(field.value)
      }))
    };
  };

  const completeness = calculateCompleteness();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Profile Completeness</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span>Progress</span>
              <span className="font-medium">{completeness.percentage}%</span>
            </div>
            <Progress 
              value={completeness.percentage} 
              className="h-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              {completeness.completedFields} of {completeness.totalFields} sections completed
            </p>
          </div>

          {/* Checklist */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Complete your profile:</h4>
            <div className="space-y-1">
              {completeness.fields.map((field, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  {field.completed ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Circle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className={field.completed ? "text-green-700" : "text-gray-600"}>
                    {field.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Completion Message */}
          {completeness.percentage === 100 ? (
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800 font-medium">
                🎉 Your profile is complete!
              </p>
              <p className="text-xs text-green-600 mt-1">
                Students can now see all your information and are more likely to book sessions with you.
              </p>
            </div>
          ) : (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">
                Complete your profile to attract more students
              </p>
              <p className="text-xs text-blue-600 mt-1">
                A complete profile increases your visibility and booking rate.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TutorProfileCompletenessSection;